import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, CalendarDays, ArrowRight, Hammer, ClipboardList, Gauge } from "lucide-react";
import { toast } from "sonner";
import { MOE_PHASES, euro } from "@/data/partnerConfig";
import { useProjects } from "@/engine/projects";
import { usePartner } from "../PartnerContext";
import { PageHeader, Btn, Segmented, Pill, SlideOver, TypeBadge, Placeholder } from "../ui";

export const PhaseStepper = ({ phase, compact = false }) => {
  const idx = MOE_PHASES.indexOf(phase);
  return (
    <ol className={`flex items-center gap-1 ${compact ? "" : "flex-wrap gap-y-2"}`} aria-label="Phases du projet">
      {MOE_PHASES.map((p, i) => (
        <li key={p} className="flex items-center gap-1" title={p}>
          <span
            className={`block rounded-full ${compact ? "h-1.5 w-5" : "h-2 w-7 sm:w-9"} ${i < idx ? "bg-petrol/45" : i === idx ? "bg-petrol" : "bg-[#E3E8E9]"}`}
          />
        </li>
      ))}
    </ol>
  );
};

// Avant démarrage du chantier
const IN_PROGRESS_PHASES = ["Découverte", "Conception", "Plans & démarches", "Consultation des entreprises", "Préparation chantier"];
// Chantier en cours (jusqu'à clôture définitive)
const ON_SITE_PHASES = ["Chantier", "Réception", "Terminé"];

const PROJECT_TYPE_LABELS = { NEUVE: "Construction / Extension", RENOVATION: "Rénovation" };
const projectTypeLabel = (p) => PROJECT_TYPE_LABELS[p.projectType] || "—";

const ProjectCard = ({ p, onSite = false }) => (
  <Link
    to={`/partenaire/projets/${p.ref}`}
    data-testid={`project-card-${p.ref}`}
    className="block rounded-2xl border border-[#E3E8E9] bg-white p-5 transition-colors hover:border-petrol/40"
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-mono text-[11px] font-bold text-petrol">{p.ref}</p>
        <p className="mt-0.5 truncate font-semibold text-navy">{p.name}</p>
        <p className="mt-0.5 text-xs text-stone-500">{p.client}</p>
      </div>
      <Pill tone="petrol">{p.phase}</Pill>
    </div>

    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
      <span data-testid={`project-card-${p.ref}-type`}>{projectTypeLabel(p)}</span>
      <span className="font-semibold text-navy">{euro(p.budget)}</span>
    </div>

    {onSite && (
      <div className="mt-3" data-testid={`project-card-${p.ref}-progress`}>
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 font-semibold text-stone-500"><Gauge className="h-3 w-3 text-petrol" /> Avancement chantier</span>
          <span className="font-display text-sm font-medium text-petrol">{p.progress ?? 0} %</span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#E9EDEE]">
          <div className="h-full rounded-full bg-petrol" style={{ width: `${p.progress ?? 0}%` }} />
        </div>
      </div>
    )}

    <p className="mt-3 flex items-center gap-1.5 text-xs text-stone-500">
      <CalendarDays className="h-3.5 w-3.5 text-petrol" /> {onSite ? "Prochaine intervention" : "Prochain RDV"} : {p.nextMeeting}
    </p>
    <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-petrol"><ArrowRight className="h-3.5 w-3.5" /> {p.nextStep}</p>
  </Link>
);

export default function PartnerProjects() {
  const [tab, setTab] = useState("moe");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { partner } = usePartner();
  const PROJECTS = useProjects();
  const inProgress = PROJECTS.filter((p) => IN_PROGRESS_PHASES.includes(p.phase));
  const onSite = PROJECTS.filter((p) => ON_SITE_PHASES.includes(p.phase));

  return (
    <div data-testid="partner-projects">
      <PageHeader
        eyebrow="Pilotage"
        title="Projets"
        sub="Les projets avec maîtrise d'œuvre et les dossiers de petits travaux sont gérés séparément : deux métiers, deux parcours."
        actions={
          <Btn testid="project-create-open" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nouveau projet
          </Btn>
        }
      />

      <Segmented
        testid="project-tab"
        value={tab}
        onChange={setTab}
        options={[
          { value: "moe", label: `Projets avec maîtrise d'œuvre (${PROJECTS.length})` },
          { value: "small", label: "Petits travaux / sans maîtrise d'œuvre" },
        ]}
      />

      {tab === "moe" ? (
        <div className="mt-4 space-y-9">
          <section data-testid="projects-in-progress-section">
            <h2 className="font-display text-xl font-medium text-navy sm:text-2xl">Projets en cours ({inProgress.length})</h2>
            {inProgress.length === 0 ? (
              <Placeholder testid="projects-in-progress-empty">Aucun dossier en amont du chantier pour le moment.</Placeholder>
            ) : (
              <div data-testid="project-list-in-progress" className="mt-3 grid gap-3 md:grid-cols-2">
                {inProgress.map((p) => <ProjectCard key={p.ref} p={p} />)}
              </div>
            )}
          </section>

          <section data-testid="projects-on-site-section">
            <h2 className="font-display text-xl font-medium text-navy sm:text-2xl">Chantiers en cours ({onSite.length})</h2>
            {onSite.length === 0 ? (
              <Placeholder testid="projects-on-site-empty">Aucun chantier en cours pour le moment.</Placeholder>
            ) : (
              <div data-testid="project-list-on-site" className="mt-3 grid gap-3 md:grid-cols-2">
                {onSite.map((p) => <ProjectCard key={p.ref} p={p} onSite />)}
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="mt-4" data-testid="project-small-list">
          <Placeholder testid="project-small-redirect">
            Vos dossiers de petits travaux sont gérés dans un espace dédié, avec devis, artisan et facturation.
          </Placeholder>
          <Link
            to="/partenaire/petits-travaux"
            data-testid="project-small-all"
            className="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-[#D9E0E2] p-5 text-sm font-bold text-cognac-dark hover:bg-[#FBF6F2]"
          >
            Ouvrir la page Petits travaux <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <SlideOver open={creating} onClose={() => setCreating(false)} testid="project-create" eyebrow="Nouveau projet" title="Quel type de projet ?">
        <div className="space-y-3">
          <button
            data-testid="project-create-moe"
            onClick={() => { setCreating(false); toast.info("La création complète d'un projet MOE sera connectée dans la phase suivante."); }}
            className="w-full rounded-2xl border-2 border-[#E3E8E9] p-5 text-left transition-colors hover:border-petrol"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-petrol-tint text-petrol"><ClipboardList className="h-5 w-5" /></span>
            <p className="mt-3 font-display text-xl font-medium text-navy">A. Projet avec maîtrise d'œuvre</p>
            <p className="mt-1 text-sm text-stone-500">Mission complète : conception, démarches, consultation, chantier, réception. Suivi en 8 phases.</p>
            <div className="mt-3"><TypeBadge type="moe" /></div>
          </button>
          <button
            data-testid="project-create-small"
            onClick={() => { setCreating(false); navigate("/partenaire/petits-travaux"); }}
            className="w-full rounded-2xl border-2 border-[#E3E8E9] p-5 text-left transition-colors hover:border-cognac"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cognac/10 text-cognac-dark"><Hammer className="h-5 w-5" /></span>
            <p className="mt-3 font-display text-xl font-medium text-navy">B. Petits travaux / sans maîtrise d'œuvre</p>
            <p className="mt-1 text-sm text-stone-500">Intervention ciblée : besoin, artisan, devis, travaux, facture. Dossier léger.</p>
            <div className="mt-3"><TypeBadge type="small" /></div>
          </button>
          <div className="rounded-xl bg-petrol-bg p-4 text-xs leading-relaxed text-stone-500" data-testid="project-ref-rule">
            <p className="font-bold text-navy">Identifiant projet</p>
            <p className="mt-1">
              Format <span className="font-mono font-bold text-petrol">{partner.code}-26-0XX</span> : code partenaire attribué par CozyHome Central,
              année sur 2 chiffres, numéro de dossier automatique (001 → 999, remis à 001 chaque année). Unique, non modifiable, sans nom client.
            </p>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}
